import { jwtDecode, JwtPayload } from "jwt-decode";
import { useStore } from "@shared/store/useStore";
import { AuthProfileSerializer } from "../types";
import { AuthJwtRepository } from "../AuthJwtRepository";
import { AuthRepository } from "../AuthRepository";
import { AuthProfileRepository } from "../AuthProfileRepository";
import { IAuthStrategy } from "../interfaces/IAuthStrategy";

export class JwtClient implements IAuthStrategy {
  private authRepo: AuthRepository;

  private authProfileRepo: AuthProfileRepository;

  private jwtRepo: AuthJwtRepository;

  private token: string | undefined;

  constructor() {
    const { jwt } = useStore.getState();
    this.authRepo = new AuthRepository();
    this.authProfileRepo = new AuthProfileRepository();
    this.jwtRepo = new AuthJwtRepository();
    this.token = jwt;
  }

  private setJwt(token: string | undefined): void {
    this.token = token;
    useStore.setState({ jwt: token });
  }

  private static setUser(user: AuthProfileSerializer | undefined): void {
    useStore.setState({ user });
  }

  private setAuthToken(token: string): void {
    this.setJwt(token);
  }

  private removeAuthToken(): void {
    this.setJwt(undefined);
  }

  public async login(
    email: string,
    password: string,
    {
      otp_token,
      captcha_token,
    }: {
      otp_token?: string;
      captcha_token: string | undefined;
    }
  ) {
    this.removeAuthToken();
    const { token } = await this.jwtRepo.createToken({
      email,
      password,
      otp_token,
      captcha_token,
    });
    this.setAuthToken(token);

    const user = await this.authProfileRepo.retrieve();
    JwtClient.setUser(user);

    return user;
  }

  public async loginWithGoogle(code: string) {
    this.removeAuthToken();

    const data = await this.authRepo.loginWithGoogle(code);
    const { token, user } = data;

    this.setAuthToken(token);

    // TODO check if this approach works, if not we can call the `await this.authProfileRepo.retrieve();`
    JwtClient.setUser(user);

    return user;
  }

  public async loginWithFacebook(access_token: string) {
    this.removeAuthToken();

    const data = await this.authRepo.loginWithFacebook(access_token);
    const { token, user } = data;

    this.setAuthToken(token);

    // TODO check if this approach works, if not we can call the `await this.authProfileRepo.retrieve();`
    JwtClient.setUser(user);

    return user;
  }

  public async logout() {
    return this.authRepo.logout().finally(() => {
      this.setJwt(undefined);
      JwtClient.setUser(undefined);
    });
  }

  public async checkAuth() {
    if (!this.token) {
      return;
    }
    const { token } = this;
    const tokenProps = jwtDecode<JwtPayload>(token);
    const now = new Date().getTime();
    const tokenIsExpired =
      tokenProps &&
      tokenProps.exp &&
      // convert to milliseconds to match `now`
      now >= tokenProps.exp * 1000;

    if (tokenIsExpired) {
      // attempt to refresh token if it's expired
      try {
        const { token: newToken } = await this.jwtRepo.refresh(token);
        this.setAuthToken(newToken);
      } catch (e) {
        this.removeAuthToken();
      }
    }
  }
}
